import React, { useCallback, useEffect, useRef, useState } from 'react'
import Grid from '@mui/material/Grid2';
import { useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight, faArrowRight, faArrowUpRightFromSquare, faBriefcase, faCheck, faCircleUp, faEnvelope, faMagnifyingGlass, faPhone, faXmark } from '@fortawesome/free-solid-svg-icons';
import { Slider } from '@mui/material';
import { apiService } from '../../services/apiService';
import { toast } from 'react-toastify';
import { faCopy } from '@fortawesome/free-regular-svg-icons';

const SESSION_KEY = "jobBoardListState";

const saveStateToSession = (state) => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  } catch (err) {
    console.error("Failed to save state to sessionStorage:", err);
  }
};

const loadStateFromSession = () => {
  try {
    const data = sessionStorage.getItem(SESSION_KEY);
    return data ? JSON.parse(data) : {};
  } catch (err) {
    console.error("Failed to load state from sessionStorage:", err);
    return {};
  }
};

function JobList() {
    const navigate = useNavigate();

    const restored = loadStateFromSession();

    const [applyId, setApplyId] = useState("");
    const [submitloader, setSubmitloader] = useState(false);
    const [loadingDown, setLoadingDown] = useState(false);
    const isInitialRender = useRef(true);
    const hasRestoredData = useRef(!!restored.jobslist?.length);

    const [isVisible, setIsVisible] = useState(restored.isVisible ?? true);
    const [jobslist, setJobs] = useState(restored.jobslist || []);
    const [paginatedata, setPaginateData] = useState(restored.paginatedata || {});
    const [currentPage, setCurrentPage] = useState(restored.currentPage || 1);
    const [searchQuery, setSearchQuery] = useState(restored.searchQuery || "");
    const [innerSearch, setInnersearch] = useState(restored.innerSearch || "");
    const [locationQuery, setLocationQuery] = useState(restored.locationQuery || "");
    const [locationSearch, setLocationSearch] = useState(restored.locationSearch || "");
    const [pageSummary, setPagesummary] = useState(restored.pageSummary || "");
    const [shorting, setShorting] = useState(restored.shorting || "");
    const [loading, setLoading] = useState(!restored.jobslist?.length);

    const [selectedPostedDates, setSelectedPostedDates] = useState(restored.selectedPostedDates || []);
    const [selectedRoleTypes, setSelectedRoleTypes] = useState(restored.selectedRoleTypes || []);
    const [selectedSalaryTypes, setSelectedSalaryTypes] = useState(restored.selectedSalaryTypes || "");
    const [selectedPosterTypes, setSelectedPosterTypes] = useState(restored.selectedPosterTypes || []);
    const [selectedSitesTypes, setSelectedSitesTypes] = useState(restored.selectedSitesTypes || []);
    const [selectedJobTypes, setSelectedJobTypes] = useState(restored.selectedJobTypes || []);
    const [selectedSourceTypes, setSelectedSourceTypes] = useState(restored.selectedSourceTypes || []);
    const [selectedExperience, setSelectedExperience] = useState(restored.selectedExperience || []);
    const [selectedCityzenship, setSelectedCityzenship] = useState(restored.selectedCityzenship || []);
    const [salaryRange, setSalaryRange] = useState(restored.salaryRange || [0, 0]);
    const [showRange, setRangeshow] = useState(restored.selectedSalaryTypes === "unknown" ? true : false);
    const [isVisibleFloat, setIsVisibleFloat] = useState(false);
    useEffect(() => {
        const handleScroll = () => {
        // Show button after scrolling more than 100vh
        if (window.scrollY > window.innerHeight) {
            setIsVisibleFloat(true);
        } else {
            setIsVisibleFloat(false);
        }
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const [filteredSuggestions, setFilteredSuggestions] = useState([]);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const fetchSuggestions = async (query) => {
        if (!query) {
            return;
        }
    
        try {
            const response = await apiService.postRequest("locations/suggestions", { "search": query });
            if(response && response.success){
            setFilteredSuggestions(response.data || [])
            //   setSuggestions(response.tags || []); // Adjust based on API response
            }else{
                if(response && response.authenticate === false){
                    await apiService.postRequest('/auth/logout');
                    localStorage.clear();
                    sessionStorage.clear();
                    window.location.href = '/login';
                }
            }
        } catch (error) {
            console.error("Error fetching suggestions:", error);
        }
    };
    const handleInputChange = (e) => {
        const value = e.target.value;
        setLocationQuery(value);
        if (value) {
            fetchSuggestions(value);
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setLocationQuery(suggestion);
        setShowSuggestions(false);
    };
    const handleKeyDown = (e) => {
        if (e.key === "ArrowDown" && activeIndex < filteredSuggestions.length - 1) {
            setActiveIndex(activeIndex + 1);
        } else if (e.key === "ArrowUp" && activeIndex > 0) {
            setActiveIndex(activeIndex - 1);
        } else if (e.key === "Enter") {
            if (activeIndex >= 0) {
                const selected = filteredSuggestions[activeIndex];
                setLocationQuery(selected.label);
                // setLocationQuery(e.target.value)
                setShowSuggestions(false);
            }
            handleSearchLocation();
        }
    };

    const scrollToTop = () => {
        window.scrollTo({
        top: 0,
        behavior: "smooth", // smooth scrolling
        });
    };
    const loaderRef = useRef(null);

    // ----------------- Save state to session on every change -----------------
    const getCurrentState = useCallback(() => ({
        isVisible,
        jobslist,
        paginatedata,
        currentPage,
        searchQuery,
        innerSearch,
        locationQuery,
        locationSearch,
        pageSummary,
        shorting,
        selectedPostedDates,
        selectedRoleTypes,
        selectedSalaryTypes,
        selectedPosterTypes,
        selectedSitesTypes,
        selectedJobTypes,
        selectedSourceTypes,
        selectedExperience,
        selectedCityzenship,
        salaryRange,
        scrollPosition: window.scrollY
    }), [
        isVisible, jobslist, paginatedata, currentPage, searchQuery, innerSearch,
        locationQuery, locationSearch, pageSummary, shorting,
        selectedPostedDates, selectedRoleTypes, selectedSalaryTypes, selectedPosterTypes,
        selectedSitesTypes, selectedJobTypes, selectedSourceTypes, selectedExperience,
        selectedCityzenship, salaryRange
    ]);

    useEffect(() => {
        saveStateToSession(getCurrentState());
    }, [getCurrentState]);

    // ----------------- Scroll Restoration Logic -----------------
    const hasScrolledRef = useRef(false);
    const shouldRestoreScrollRef = useRef(false);

    // Check if we should restore scroll on component mount
    useEffect(() => {
        // Check for navigation back flag
        const shouldRestore = sessionStorage.getItem('shouldRestoreScroll');
        if (shouldRestore === 'true') {
            shouldRestoreScrollRef.current = true;
            sessionStorage.removeItem('shouldRestoreScroll'); // Clean up
        }
        
        // Fallback: check navigation type
        if (!shouldRestoreScrollRef.current) {
            const navigationType = performance.getEntriesByType('navigation')[0]?.type;
            shouldRestoreScrollRef.current = navigationType === 'back_forward';
        }
    }, []);

    // Handle scroll restoration
    useEffect(() => {
        if (hasScrolledRef.current) return; // Ensure scrolling happens only once
        
        const shouldScroll = shouldRestoreScrollRef.current && restored.scrollPosition > 0;
        
        if (shouldScroll) {
            const performScroll = () => {
                window.scrollTo({
                    top: restored.scrollPosition,
                    behavior: 'auto'
                });
                hasScrolledRef.current = true;
                shouldRestoreScrollRef.current = false; // Reset flag
            };

            if (restored.jobslist?.length > 0) {
                // Jobs already available from session - scroll immediately
                setTimeout(performScroll, 100);
            } else if (!loadingDown && jobslist.length > 0) {
                // Jobs loaded from API - scroll after loading complete
                setTimeout(performScroll, 200);
            }
        }
    }, [loadingDown, jobslist, restored.scrollPosition, restored.jobslist]);

    // Listen for popstate event (browser back/forward)
    useEffect(() => {
        const handlePopState = () => {
            shouldRestoreScrollRef.current = true;
            hasScrolledRef.current = false; // Reset so scroll can happen again
        };
        
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    // ----------------- Checkbox and Filter Handlers -----------------
    const handleCheckboxChange = (event, setState) => {
        const { value, checked } = event.target;
        setState((prev) => {
            const newState = checked ? [...prev, value] : prev.filter((item) => item !== value);
            setCurrentPage(1);
            return newState;
        });
    };

    const handleSalaryChange = (event, newValue) => {
        setSalaryRange(newValue);
    };

    const handleSalaryCommitted = () => {
        setCurrentPage(1);
        fetchJobs({ page: 1 });
    };

    const handleSalarytypeChange = (e) => {
        const { value } = e.target;
        setSelectedSalaryTypes(value);
        setCurrentPage(1);
        if(value === "unknown"){
            setRangeshow(true)
            setSalaryRange([0, 0]);
        }else{
            setRangeshow(false);
        }
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    // ----------------- Infinite scroll -----------------
    useEffect(() => {
        const observer = new IntersectionObserver(entries => {
            if (
                entries[0].isIntersecting &&
                !loadingDown && // only trigger if not loading
                currentPage < (paginatedata?.last_page || 1)
            ) {
                handlePageChange(currentPage + 1);
            }
        });

        if (loaderRef.current) observer.observe(loaderRef.current);

        return () => observer.disconnect();
    }, [currentPage, paginatedata, loadingDown]);


    // ----------------- Fetch jobs -----------------
    const fetchJobs = useCallback(async (params = {}) => {
        setLoadingDown(true);
        try {
            const salaryParams =
                salaryRange[0] === 0 && salaryRange[1] === 0
                ? {}
                : { salary_min: salaryRange[0], salary_max: salaryRange[1] };

            const defaultParams = {
                sort: shorting,
                page: currentPage,
                location: locationSearch,
                search: innerSearch,
                posted_date: selectedPostedDates.length ? selectedPostedDates.join(",") : "",
                role_type: selectedRoleTypes.length ? selectedRoleTypes.join(",") : "",
                poster_type: selectedPosterTypes.length ? selectedPosterTypes.join(",") : "",
                site_type: selectedSitesTypes.length ? selectedSitesTypes.join(",") : "",
                job_type: selectedJobTypes.length ? selectedJobTypes.join(",") : "",
                salary_type: selectedSalaryTypes.length ? selectedSalaryTypes : "",
                source: selectedSourceTypes.length ? selectedSourceTypes.join(",") : "",
                experience_range: selectedExperience.length ? selectedExperience.join(",") : "",
                citizenship_status: selectedCityzenship.length ? selectedCityzenship.join(",") : "",
                ...salaryParams,
                ...params
            };

            const queryString = new URLSearchParams(defaultParams).toString();
            const response = await apiService.getRequest(`/jobseeker/jobs?${queryString}`);

            if (response && response.success === true) {
                setJobs(prevJobs => {
                    if (currentPage === 1) {
                        return response.data;
                    }
                    const jobMap = new Map(prevJobs.map(job => [job.id, job]));
                    response.data.forEach(newJob => {
                        jobMap.set(newJob.id, { ...jobMap.get(newJob.id), ...newJob });
                    });
                    const prevIds = new Set(prevJobs.map(j => j.id));
                    const newJobs = response.data.filter(j => !prevIds.has(j.id));
                    return [...prevJobs.map(j => jobMap.get(j.id)), ...newJobs];
                });
                setPaginateData(response.meta);
                setPagesummary(response.pagination_summary);
            } else {
                if (response.authenticate === false) {
                    await apiService.postRequest('/auth/logout');
                    localStorage.clear();
                    sessionStorage.clear();
                    window.location.href = '/login';
                }
            }
        } catch (error) {
            console.error('Error fetching tab data:', error);
        } finally {
            setLoading(false);
            setLoadingDown(false);
        }
    }, [
        shorting, currentPage, innerSearch, selectedSalaryTypes, salaryRange, locationSearch,
        selectedPostedDates, selectedRoleTypes, selectedPosterTypes, selectedSitesTypes,
        selectedJobTypes, selectedSourceTypes, selectedExperience, selectedCityzenship
    ]);

    const handleSearch = () => {
        setCurrentPage(1);
        setInnersearch(searchQuery);
    };

    const handleSearchLocation = () => {
        setCurrentPage(1);
        setLocationSearch(locationQuery);
    };

    const handleShortNew = (newData) => {
        setCurrentPage(1);
        setShorting(newData);
    };

    // ----------------- Initial fetch -----------------
    useEffect(() => {
        if (!hasRestoredData.current) {
            fetchJobs();
        }
        isInitialRender.current = false;
    }, [fetchJobs]);

    useEffect(() => {
        if (isInitialRender.current && hasRestoredData.current) return;
        fetchJobs();
    }, [
        fetchJobs, shorting, innerSearch, selectedSalaryTypes, salaryRange, locationSearch,
        selectedPostedDates, selectedRoleTypes, selectedPosterTypes, selectedSitesTypes,
        selectedJobTypes, selectedSourceTypes, selectedExperience, selectedCityzenship
    ]);

    // ----------------- UI helpers -----------------
    const toggleVisibility = () => {
        setIsVisible(!isVisible);
    };

    const closeElement = () => {
        setIsVisible(true);
    };

    const navigateToJobDetails = (jobId) => {
        saveStateToSession(getCurrentState());
        sessionStorage.setItem('shouldRestoreScroll', 'true');
        navigate(`/job-board/job-details/${jobId}?job-list`);
    };

    const handleApply = async (applyid) => {
        setSubmitloader(true);
        setApplyId(applyid);
        try {
            const params = ({
                job_id : applyid
            })
            const response = await apiService.postRequest('/jobseeker/apply',params);
            if(response && response.success === true){
                setJobs((prevJobs) => prevJobs.map((job) => job.id === applyid ? { ...job, is_applied: true } : job ) );
                toast(response.message || 'Job apply successfully!');
            }else{
                toast(response.message || 'Failed, try again!');
                if(response && response.authenticate === false){
                    await apiService.postRequest('/auth/logout');
                    localStorage.clear();
                    sessionStorage.clear();
                    window.location.href = '/login';
                }
            }
        } catch (err) {
            toast.error("Failed to apply job");
        }finally{
            setSubmitloader(false);
        }
    };
    
    const handleSave = async (saveid) => {
        try {
            const params = ({
                job_id : saveid
            })
            const response = await apiService.postRequest('/jobseeker/save',params);
            if(response && response.success === true){
                setJobs((prevJobs) =>
                    prevJobs.map((job) =>
                        job.id === saveid ? { ...job, is_saved: true } : job
                    )
                );
            toast(response.message || 'Job saved successfully!');
            }else{
                toast(response.message || 'Failed, try again!');
                if(response && response.authenticate === false){
                    await apiService.postRequest('/auth/logout');
                    localStorage.clear();
                    sessionStorage.clear();
                    window.location.href = '/login';
                }
            }
        } catch (err) {
            toast.error("Failed to save job");
        }
    };
    
    const handleRemoveSave = async (saveid) => {
        try {
            const response = await apiService.deleteRequest(`/jobseeker/remove-saved/${saveid}`);
            if(response && response.success === true){
                setJobs((prevJobs) =>
                    prevJobs.map((job) =>
                        job.id === saveid ? { ...job, is_saved: false } : job
                    )
                );
                toast(response.message || 'Job Unsaved successfully!');
            }else{
                toast(response.message || 'Failed, try again!');
                if(response && response.authenticate === false){
                    await apiService.postRequest('/auth/logout');
                    localStorage.clear();
                    sessionStorage.clear();
                    window.location.href = '/login';
                }
            }
        } catch (err) {
            toast.error("Failed to Unsave job");
        }
    };

    const clearState = () => {
        sessionStorage.removeItem("jobBoardListState");
        window.location.reload();
    };

    useEffect(() => {
        const handleBeforeUnload = () => {
            sessionStorage.removeItem("jobBoardListState");
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, []);
    
    return (
        <div className='container-fluid px-0'>
            <div className={isVisible === true ? 'hide_mobitr' : 'show_mobitr'}>
                <div className='filter_jobboardmain'>
                    <div className='d-flex w-100 justify-content-end'>
                        <button className='d-md-none closecat_btn' onClick={closeElement}><FontAwesomeIcon icon={faXmark} /></button>
                    </div>
                    <div className='d-flex flex-column filter_mainjob pe-3 mt-3'>
                        <h4>Search Job</h4>
                        <div className='d-flex search_forfiltersjob'>
                            <div className='d-flex w-100 align-items-center' style={{ position: "relative" }}>
                                <div className='col-2 px-0 text-center'>
                                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                                </div>
                                <div className='col-8 px-0'>
                                    <input type='text' placeholder='Search by City and State'
                                        value={locationQuery}
                                        onChange={handleInputChange}
                                        onKeyDown={handleKeyDown}
                                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                        onFocus={() => searchQuery && setShowSuggestions(true)}
                                    />
                                    {showSuggestions && (
                                        <ul className="suggestions-dropdown">
                                            {filteredSuggestions.length > 0 ? (
                                                filteredSuggestions.map((suggestion, index) => (
                                                    <li
                                                        key={index}
                                                        className={index === activeIndex ? "active" : ""}
                                                        onMouseDown={() => handleSuggestionClick(suggestion.label)}
                                                    >
                                                        {suggestion.label}
                                                    </li>
                                                ))
                                            ) : (
                                                <li className="no-suggestions">No results found</li>
                                            )}
                                        </ul>
                                    )}
                                </div>
                                <div className='col-2 px-0 d-flex justify-content-end'>
                                    <button onClick={handleSearchLocation}><FontAwesomeIcon icon={faArrowRight} /></button>
                                </div>
                            </div>
                        </div>
                        <Grid size={{xs:12}} container flexDirection="column" className="devider_topfiljob mt-3">
                            <h5>Posted Date Range</h5>
                            {[
                                { label: "24 Hours", value: "24_hours" },
                                { label: "2 Days", value: "2_days" },
                                { label: "This Week", value: "this_week" },
                                { label: "Last Week", value: "last_week" },
                                { label: "Last 30 Days", value: "last_30_days" },
                            ].map((item) => (
                                <span key={item.value}>
                                    <input 
                                        type="checkbox" 
                                        value={item.value} 
                                        checked={selectedPostedDates.includes(item.value)}
                                        onChange={(e) => handleCheckboxChange(e, setSelectedPostedDates)} 
                                    /> {item.label}
                                </span>
                            ))}
                        </Grid>
                        <Grid size={{xs:12}} container flexDirection="column" className="devider_topfiljob">
                            <h5>Role Type</h5>
                            {[
                                { label: "Admin", value: "Admin" },
                                { label: "Developer", value: "Developer" },
                                { label: "Business Analyst", value: "Business Analyst" },
                                { label: "Consultant", value: "Consultant" },
                                { label: "Manager", value: "Manager" },
                                { label: "Product Owner", value: "Product Owner" },
                                { label: "Business System Analyst", value: "Business System Analyst" },
                                { label: "Developer/Administrator", value: "Developer/Administrator" },
                                { label: "Others", value: "Others" },
                            ].map((item) => (
                                <span key={item.value}>
                                    <input 
                                        type="checkbox" 
                                        value={item.value} 
                                        checked={selectedRoleTypes.includes(item.value)}
                                        onChange={(e) => handleCheckboxChange(e, setSelectedRoleTypes)} 
                                    /> {item.label}
                                </span>
                            ))}
                        </Grid>
                        <Grid size={{xs:12}} container flexDirection="column" className="devider_topfiljob">
                            <h5>Poster Type</h5>
                            {[
                                { label: "Staffing Company", value: "Staffing Company" },
                                { label: "Implementation Partners", value: "Implementation Partners" },
                                { label: "Direct Requirement", value: "Direct Requirement" },
                                { label: "Unknown", value: "Unknown" },
                            ].map((item) => (
                                <span key={item.value}>
                                    <input 
                                        type="checkbox" 
                                        value={item.value} 
                                        checked={selectedPosterTypes.includes(item.value)}
                                        onChange={(e) => handleCheckboxChange(e, setSelectedPosterTypes)} 
                                    /> {item.label}
                                </span>
                            ))}
                        </Grid>
                        <Grid size={{xs:12}} container flexDirection="column" className="devider_topfiljob">
                            <h5>Site Type</h5>
                            {[
                                { label: "Remote", value: "Remote" },
                                { label: "Onsite", value: "Onsite" },
                                { label: "Hybrid", value: "Hybrid" },
                                { label: "Unknown", value: "unknown" },
                            ].map((item) => (
                                <span key={item.value}>
                                    <input 
                                        type="checkbox" 
                                        value={item.value} 
                                        checked={selectedSitesTypes.includes(item.value)}
                                        onChange={(e) => handleCheckboxChange(e, setSelectedSitesTypes)} 
                                    /> {item.label}
                                </span>
                            ))}
                        </Grid>
                        <Grid size={{xs:12}} container flexDirection="column" className="devider_topfiljob">
                            <h5>Job Type</h5>
                            {[
                                { label: "Full-time", value: "Full-time" },
                                { label: "Part-time", value: "Part-time" },
                                { label: "Contract", value: "Contract" },
                                { label: "Temporary", value: "Temporary" },
                                { label: "Internship", value: "Internship" },
                                { label: "Unknown", value: "unknown" },
                            ].map((item) => (
                                <span key={item.value}>
                                    <input 
                                        type="checkbox" 
                                        value={item.value} 
                                        checked={selectedJobTypes.includes(item.value)}
                                        onChange={(e) => handleCheckboxChange(e, setSelectedJobTypes)} 
                                    /> {item.label}
                                </span>
                            ))}
                        </Grid>
                        <Grid size={{xs:12}} container flexDirection="column" className="devider_topfiljob">
                            <span>Salary Type</span>
                            <select className='joboard_typeselect' value={selectedSalaryTypes} onChange={handleSalarytypeChange}>
                                <option value="">Select Type</option>
                                <option value="hourly">Hourly</option>
                                <option value="yearly">Yearly</option>
                                <option value="unknown">Unknown</option>
                            </select>
                            {!showRange && <>
                            <h5>Salary Range</h5>
                            <h6>${salaryRange[0]} - ${salaryRange[1]}</h6>
                            <div className='px-4 slider_colormain'>
                            <Slider
                                getAriaLabel={() => 'Salary range'}
                                value={salaryRange}
                                onChange={handleSalaryChange}
                                onChangeCommitted={handleSalaryCommitted}
                                valueLabelDisplay="auto"
                                getAriaValueText={(value) => `${value} USD`}
                                min={0} // Minimum salary
                                max={200000} // Maximum salary
                            />
                            </div>
                            <small className='resetprice_rangesmall' onClick={()=> setSalaryRange([0,0])}>Reset Price Range</small>
                            </>}
                        </Grid>
                        <Grid size={{xs:12}} container flexDirection="column" className="devider_topfiljob">
                            <h5>Source</h5>
                            {[
                                { label: "TekP Intelligence", value: "TekP Intelligence" },
                                { label: "LinkedIn", value: "LinkedIn" },
                                { label: "Monster", value: "Monster" },
                                { label: "Indeed", value: "Indeed" },
                                { label: "Glassdoor", value: "Glassdoor" },
                                { label: "Dice", value: "Dice" },
                                { label: "Techfetch", value: "Techfetch" },
                                { label: "Other", value: "Other" },
                            ].map((item) => (
                                <span key={item.value}>
                                    <input 
                                        type="checkbox" 
                                        value={item.value} 
                                        checked={selectedSourceTypes.includes(item.value)}
                                        onChange={(e) => handleCheckboxChange(e, setSelectedSourceTypes)} 
                                    /> {item.label}
                                </span>
                            ))}
                        </Grid>
                        <Grid size={{xs:12}} container flexDirection="column" className="devider_topfiljob">
                            <h5>Experience Level</h5>
                            {[
                                { label: "No experience", value: "No experience" },
                                { label: "0 to 2 years", value: "0 to 2 Years" },
                                { label: "2 to 5 years", value: "2 to 5 Years" },
                                { label: "5 to 8+ years", value: "5 to 8+ Years" },
                                { label: "8 to 12+ years", value: "8 to 12+ Years" },
                                { label: "15+ years", value: "15+ Years" },
                                { label: "Unknown", value: "unknown" },
                            ].map((item) => (
                                <span key={item.value}>
                                    <input 
                                        type="checkbox" 
                                        value={item.value} 
                                        checked={selectedExperience.includes(item.value)}
                                        onChange={(e) => handleCheckboxChange(e, setSelectedExperience)} 
                                    /> {item.label}
                                </span>
                            ))}
                        </Grid>
                        <Grid size={{xs:12}} container flexDirection="column" className="devider_topfiljob">
                            <h5>Citizenship Status</h5>
                            {[
                                { label: "CPT", value: "CPT" },
                                { label: "GC", value: "GC" },
                                { label: "GC EAD", value: "GC EAD" },
                                { label: "H1B", value: "H1B" },
                                { label: "H4 EAD", value: "H4 EAD" },
                                { label: "L2 EAD", value: "L2 EAD" },
                                { label: "OPT EAD", value: "OPT EAD" },
                                { label: "USC", value: "USC" },
                                { label: "Unknown", value: "unknown" },
                            ].map((item) => (
                                <span key={item.value}>
                                    <input 
                                        type="checkbox" 
                                        value={item.value} 
                                        checked={selectedCityzenship.includes(item.value)}
                                        onChange={(e) => handleCheckboxChange(e, setSelectedCityzenship)} 
                                    /> {item.label}
                                </span>
                            ))}
                        </Grid>
                        <Grid size={{xs:12}} container flexDirection="column" className="devider_topfiljob">
                            <button className='clear_filbtn_jobs' onClick={clearState}>Clear Filter</button>
                        </Grid>
                    </div>
                </div>
            </div>
            <div className='content_container_jobboard'>
                <Grid size={{sm:12}} container className="main_jobseeker">
                    <Grid size={{md:12,xs:12}} container flexDirection="column" className="jobseekerhead">
                        <h3>Job Board</h3>
                        <span><b onClick={()=>navigate("/dashboard")}>Dashboard</b> <FontAwesomeIcon className='angleright' icon={faAngleRight} /> Job Board</span>
                        <button onClick={toggleVisibility} className='d-md-none main_category_btn'>
                            Filter
                        </button>
                    </Grid>
                    <Grid size={{xs:12}} container>
                        <Grid size={{ xs: 12 }} container justifyContent="space-between" alignItems="center" className="mt-4">
                            <Grid size={{ xs: 12 }} container justifyContent="flex-end">
                                <button className='registersendbtn2 my-0 px-4' style={{width:"max-content"}} onClick={()=> navigate("/job-board/my-job")}><FontAwesomeIcon icon={faBriefcase} /> My Submission</button>
                            </Grid>
                            <Grid size={{ sm:6 ,xs: 12 }} container flexDirection="column">
                                <div className='d-flex jobseekersearch my-3'>
                                    <div className='col-md-9 input_jobssearch'>
                                        <FontAwesomeIcon className='iconcol' icon={faMagnifyingGlass} />
                                        <input type='text' placeholder='search'
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                handleSearch(); // Trigger search on Enter key press
                                            }
                                        }}/>
                                    </div>
                                    <div className='col-md-3 col-4'>
                                        <button onClick={handleSearch}>Search</button>
                                    </div>
                                </div>
                            </Grid>
                            <Grid size={{ sm:4,xs: 12 }} container alignItems="center" className="gap-3" justifyContent="end">
                                <div className='d-flex my-3 short_byonboard'>
                                    <label>Sort by: </label>
                                    <select className='ms-2' onChange={(e) => handleShortNew(e.target.value)} value={shorting}>
                                        <option value="newest">Newest</option>
                                        <option value="oldest">Oldest</option>
                                    </select>
                                </div>
                            </Grid>
                        </Grid>
                        {loading ? 
                        <div className='d-flex justify-content-center w-100 mt-5 pt-5'>
                            <img style={{ width: "100px" }} src={require('../../assets/pinkloader.gif')} alt='Loading...' />
                        </div> :<>
                            <Grid size={{ xs:12 }}  container justifyContent="center" className="my-4">
                            {isVisibleFloat && (
                                <button className='d-flex align-items-center justify-content-center'
                                onClick={scrollToTop}
                                style={{
                                    position: "fixed",
                                    bottom: "20px",
                                    width:"40px",
                                    height:"40px",
                                    right: "20px",
                                    padding: "0px",
                                    borderRadius: "100px",
                                    border: "none",
                                    backgroundColor: "#C32A7C",
                                    color: "white",
                                    fontSize: "18px",
                                    cursor: "pointer",
                                    zIndex: 999,
                                }}
                                >
                                    <FontAwesomeIcon icon={faCircleUp} />
                                </button>
                            )}
                            <span className='w-100 total_jobs'>{pageSummary}</span>
                            {jobslist && jobslist.map((jobslist,index) =>(
                                <Grid size={{xs:12}} key={index} container className="main_cardjob mt-3">
                                    <Grid size={{xs:12}} container justifyContent="space-between">
                                        <Grid container className="titleadmin_joblist">
                                            {jobslist && jobslist.source === "LinkedIn" ? 
                                                <img src={require("../../assets/sample/linkedin.svg").default} alt='Job Icon'/>:
                                                jobslist.source === "Monster" ?
                                                <img src={require("../../assets/sample/monster.svg").default} alt='Job Icon'/>:
                                                jobslist.source === "Indeed" ?
                                                <img src={require("../../assets/sample/indeed.svg").default} alt='Job Icon'/>:
                                                jobslist.source === "Glassdoor" ?
                                                <img src={require("../../assets/sample/glassdoor.svg").default} alt='Job Icon'/>:
                                                jobslist.source === "Dice" ?
                                                <img src={require("../../assets/sample/dice.svg").default} alt='Job Icon'/>:
                                                jobslist.source === "Techfetch" ?
                                                <img src={require("../../assets/sample/techfetch.svg").default} alt='Job Icon'/>:
                                                jobslist.source === "TekP Intelligence" ?
                                                <img src={require("../../assets/sample/tekp.svg").default} alt='Job Icon'/>:
                                                <img src={require("../../assets/sample/others.svg").default} alt='Job Icon'/>}
                                            
                                            <div className='jobboard_listtitle'>
                                                <Link className='link_joblist' to={`/job-board/job-details/${jobslist.job_id}?job-list`} onClick={() => { saveStateToSession(getCurrentState()); sessionStorage.setItem('shouldRestoreScroll', 'true'); }}><h5 className='tittle_jobswrap'>{jobslist.title}</h5></Link>
                                                <h6 className='mb-0'>{jobslist && jobslist.location && jobslist.location !== "" && jobslist.location !== "unknown" && jobslist.location}{jobslist && jobslist.location && jobslist.location !== "" && jobslist.location !== "unknown" && jobslist.recruiter_company && jobslist.recruiter_company !== "" && jobslist.recruiter_company !== "unknown" && ' -'} {jobslist && jobslist.recruiter_company && jobslist.recruiter_company !== "" && jobslist.recruiter_company !== "unknown" && jobslist.recruiter_company }</h6>
                                            </div>
                                        </Grid>
                                        <Grid container alignItems="center" justifyContent="flex-end" className="endcardjobboard">
                                            {/* <span className='total_jobs'>Job Id : <span>{jobslist.job_id}</span></span> */}
                                            {jobslist && jobslist.recruiter_email && jobslist.recruiter_email[0] !== "" && jobslist.recruiter_email[0] !== "unknown" &&
                                                <span className="hover_showes">
                                                    <FontAwesomeIcon icon={faEnvelope} />
                                                    <span>
                                                    {jobslist.recruiter_email[0]}
                                                    {jobslist.recruiter_email.length > 1 && " ..."}
                                                    <button
                                                        onClick={() => {
                                                        const copyText = jobslist.recruiter_email.join(", ");
                                                        navigator.clipboard.writeText(copyText);
                                                        toast("Mail ID(s) copied to clipboard");
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faCopy} />
                                                    </button>
                                                    </span>
                                                </span>
                                            }
                                            {jobslist && jobslist.recruiter_phone && jobslist.recruiter_phone !== "" && jobslist.recruiter_phone !== "unknown" &&
                                                <span className='hover_showes'><FontAwesomeIcon icon={faPhone} />
                                                    <span>{jobslist.recruiter_phone} <button onClick={() => {navigator.clipboard.writeText(`${jobslist.recruiter_phone}`); toast('Phone Number copied to clipboard'); }}><FontAwesomeIcon icon={faCopy} /></button></span>
                                                </span>
                                            }
                                            <small className='d-flex align-items-center'>
                                                {jobslist && jobslist.is_saved === true ?
                                                <span className='save_jobsspan me-3' onClick={() => handleRemoveSave(jobslist.id)}>
                                                    <img src={require("../../assets/sample/save_tagpink.svg").default} alt="save"/>
                                                </span> :
                                                <span className='save_jobsspan me-3' onClick={() => handleSave(jobslist.id)}>
                                                    <img src={require("../../assets/sample/save_tagblack.svg").default} alt="save"/>
                                                </span>}
                                            </small>
                                        </Grid>
                                    </Grid>
                                    <Grid size={{sm:12,xs:12}} container className="spans_icons column-gap-5 mt-4">
                                        {jobslist && jobslist.site_type && jobslist.site_type !== "" && jobslist.site_type !== "unknown" &&
                                        <Grid container alignItems="center">
                                            <span className='d-flex align-items-center'><img src={require("../../assets/jobicons/onsite.svg").default} alt='onsite'/>
                                                <span className='role_typejobicon'>
                                                    <small>Site Type</small>
                                                    <span>{jobslist.site_type}</span>
                                                </span>
                                            </span>
                                        </Grid>}
                                        {jobslist && jobslist.experience_range && jobslist.experience_range !== "" && jobslist.experience_range !== "unknown" && jobslist.experience_range !== "Unknown" &&
                                        <Grid container alignItems="center">
                                            <span className='d-flex align-items-center'><img src={require("../../assets/jobicons/briefcase.svg").default} alt='briefcase'/>
                                                <span className='role_typejobicon'>
                                                    <small>Experience</small>
                                                    <span>{jobslist.experience_range}</span>
                                                </span>
                                            </span>
                                        </Grid>}
                                        {jobslist && jobslist.job_type && jobslist.job_type !== "" && jobslist.job_type !== "unknown" &&
                                        <Grid container alignItems="center">
                                            <span className='d-flex align-items-center'><img src={require("../../assets/jobicons/jobtype.svg").default} alt='jobtype'/>
                                                <span className='role_typejobicon'>
                                                    <small>Type</small>
                                                    <span>{jobslist.job_type || "No data"}</span>
                                                </span>
                                            </span>
                                        </Grid>}
                                        {jobslist && jobslist.certifications && jobslist.certifications !== "" && jobslist.certifications !== "unknown" &&
                                        <Grid container alignItems="center">
                                            <span className='d-flex align-items-center'><img src={require("../../assets/jobicons/certificate.svg").default} alt='certificate'/>
                                                <span className='role_typejobicon'>
                                                    <small>Certifications</small>
                                                    <span>{jobslist.certifications || "No data"}</span>
                                                </span>
                                            </span>
                                        </Grid>}
                                        {jobslist && (jobslist.citizenship_status !== null || jobslist.restricted_citizenship_status !== null) &&  <>
                                        {jobslist && (jobslist.citizenship_status !== "unknown" || jobslist.restricted_citizenship_status !== "unknown") &&
                                        <Grid container alignItems="center">
                                            <span className='d-flex align-items-center'><img src={require("../../assets/jobicons/visa.svg").default} alt='certificate'/>
                                                <span className='role_typejobicon'>
                                                    <small>Visa</small>
                                                    <div className='d-flex gap-4'>
                                                        <span><FontAwesomeIcon className='iconpinkssets' icon={faCheck} />{jobslist.citizenship_status === "unknown" ? "-" :<> {jobslist.citizenship_status || "-"}</>}</span>
                                                        <span><FontAwesomeIcon className='iconpinkssets' icon={faXmark} /> {jobslist.restricted_citizenship_status === "unknown" ? "-" :<> {jobslist.restricted_citizenship_status || "-"}</>}</span>
                                                    </div>
                                                </span>
                                            </span>
                                        </Grid>}
                                        </>}
                                    </Grid>
                                    <Grid size={{xs:12}} container alignItems="flex-end" justifyContent="space-between" className="spans_tagsjob mb-3 gap-3">
                                        <small>{jobslist.created_at} <span className='job_idshowbox ms-3'>Job Id : <span>{jobslist.job_id || 0}</span></span> <span className='job_idshowbox ms-3'>Applicants : <span>{jobslist?.job_post_application_count || 0}</span></span><span className='job_idshowbox ms-3'>Applicants Seen : <span>{jobslist.job_post_view_count || 0}</span></span></small>
                                        <Grid container className="gap-3" alignItems="center">
                                            <button className='my-0 registersendbtn1' onClick={()=> navigateToJobDetails(jobslist.job_id)}>View More</button>
                                            {jobslist && jobslist.is_applied === true ?
                                                <button className='disabled_btnapply my-0'>Applied</button>:<>
                                                {jobslist && jobslist.apply_url && jobslist.apply_url !== "" && jobslist.apply_url !== null ? 
                                                    <a className='registersendbtn2 my-0 register_flexatags' href={`${jobslist.apply_url}`} target="_blank" rel="noopener noreferrer">Apply <FontAwesomeIcon icon={faArrowUpRightFromSquare} /></a>
                                                    :
                                                    <button className='registersendbtn2 my-0' onClick={() => handleApply(jobslist.id)}>{submitloader && jobslist.id === applyId ? <img src={require('../../assets/whiteloader.gif')} alt=''/> : <>Apply <FontAwesomeIcon icon={faArrowUpRightFromSquare} /></>}</button>
                                                }</>
                                            }
                                        </Grid>
                                    </Grid>
                                </Grid>
                            ) )}
                            {loadingDown && 
                                <div className='d-flex justify-content-center w-100 mt-5 pt-5'>
                                    <img style={{ width: "100px" }} src={require('../../assets/pinkloader.gif')} alt='Loading...' />
                                </div>
                            }
                            <div ref={loaderRef} style={{ height: '50px' }} />
                            </Grid>
                        </>}
                    </Grid>
                </Grid>
            </div>
        </div>
    )
}

export default JobList